import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LinkOutlined, PlayCircleOutlineOutlined, CollectionsOutlined,
  CodeOutlined, ChatBubbleOutlineOutlined, OndemandVideoOutlined, ForumOutlined,
  GridViewOutlined, StorefrontOutlined, DeleteOutlineOutlined, SearchOutlined,
  VideocamOutlined, CropOutlined, FitScreenOutlined,
  TextFieldsOutlined, ImageOutlined,
} from '@mui/icons-material';
import {
  PRIMARY, LOBBY_BREAKPOINTS,
  resolveHotspotStyleForBp, hasHotspotStyleBpOverride,
  setHotspotStyleBpOverride, hasHotspotTransitionBpOverride,
  setHotspotTransitionBpOverride, resolveHotspotTransitionForBp,
  clearHotspotStyleBpOverride,
} from '../constants';
import { ContentBrowserModal } from '../ContentBrowserModal';
import { ColorOpacityPopover, WidthPopover, PortalPopover, ImagePopover, FontPopover } from './CanvasPopovers';
import { ThumbPickerInline } from './CanvasHelpers';

const CHAT_COLOR = '#0a7aff';

// ── Hotspot type options ──────────────────────────────────────────────────────
const HOTSPOT_TYPES = [
  { key: 'link',             label: 'Link To',           icon: <LinkOutlined sx={{ fontSize: 14 }} />,              color: '#1075BA' },
  { key: 'video',            label: 'Video Pop-up',      icon: <PlayCircleOutlineOutlined sx={{ fontSize: 14 }} />, color: '#e63946' },
  { key: 'gallery',          label: 'Gallery Pop-up',    icon: <CollectionsOutlined sx={{ fontSize: 14 }} />,       color: '#7209b7' },
  { key: 'iframe',           label: 'iFrame Pop-up',     icon: <CodeOutlined sx={{ fontSize: 14 }} />,              color: '#2d9e4e' },
  { key: 'chat',             label: 'Live Chat',         icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 14 }} />, color: '#0a7aff' },
  { key: 'stream',           label: 'Livestream Player', icon: <OndemandVideoOutlined sx={{ fontSize: 14 }} />,     color: '#EF4444' },
  { key: 'livestream-chat',  label: 'Livestream Chat',   icon: <ForumOutlined sx={{ fontSize: 14 }} />,             color: '#0a7aff' },
  { key: 'exhibitor-grid',   label: 'Exhibitor Grid',    icon: <GridViewOutlined sx={{ fontSize: 14 }} />,          color: '#0d7a5f' },
];

function TypeDropdown({ value, onChange, stopAll }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [pos, setPos] = useState(null);

  const active = HOTSPOT_TYPES.find(t => t.key === value) || HOTSPOT_TYPES[0];

  const openDropdown = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ bottom: window.innerHeight - r.top + 6, left: r.left });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) setOpen(false);
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [open]);

  return (
    <Box sx={{ position: 'relative' }} onMouseDown={stopAll} onClick={stopAll}>
      <Box
        ref={triggerRef}
        onClick={() => open ? setOpen(false) : openDropdown()}
        sx={{
          height: 30, display: 'flex', alignItems: 'center', gap: '6px',
          px: 0.75, pr: 1.25,
          border: `1px solid ${open ? PRIMARY : '#e4e9f0'}`,
          borderRadius: '7px', cursor: 'pointer',
          background: open ? alpha(PRIMARY, 0.06) : '#f5f7fa',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          transition: 'all 0.13s', userSelect: 'none',
          '&:hover': { borderColor: alpha(PRIMARY, 0.4), background: alpha(PRIMARY, 0.04) },
        }}
      >
        <Box sx={{ width: 20, height: 20, borderRadius: '5px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active.color, color: '#fff' }}>
          {active.icon}
        </Box>
        <Box sx={{ fontSize: 12, fontWeight: 600, color: '#2c3444', whiteSpace: 'nowrap' }}>
          {active.label}
        </Box>
        <Box sx={{ ml: 0.5, fontSize: 9, color: '#9ea8b3', lineHeight: 1, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</Box>
      </Box>

      {open && pos && ReactDOM.createPortal(
        <Box
          onMouseDown={stopAll}
          onClick={stopAll}
          sx={{
            position: 'fixed', bottom: pos.bottom, left: pos.left,
            minWidth: 175,
            background: '#fff', border: '1px solid #e4e9f0',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            py: '4px', zIndex: 9999, overflow: 'hidden',
          }}
        >
          {HOTSPOT_TYPES.map(t => {
            const isActive = t.key === value;
            return (
              <Box
                key={t.key}
                onClick={() => { onChange(t.key); setOpen(false); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  px: 1.25, py: '7px', cursor: 'pointer',
                  background: isActive ? alpha(t.color, 0.07) : 'transparent',
                  transition: 'background 0.12s',
                  '&:hover': { background: isActive ? alpha(t.color, 0.1) : '#f5f7fa' },
                }}
              >
                <Box sx={{ width: 24, height: 24, borderRadius: '6px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isActive ? t.color : alpha(t.color, 0.12), color: isActive ? '#fff' : t.color, transition: 'all 0.12s' }}>
                  {t.icon}
                </Box>
                <Box sx={{ fontSize: 12.5, fontWeight: isActive ? 700 : 500, color: isActive ? t.color : '#2c3444', flex: 1 }}>
                  {t.label}
                </Box>
                {isActive && <Box sx={{ fontSize: 11, color: t.color, fontWeight: 800 }}>✓</Box>}
              </Box>
            );
          })}
        </Box>,
        document.body
      )}
    </Box>
  );
}


const isUrl = str => {
  if (!str) return false;
  return /^https?:\/\//i.test(str.trim()) || /^www\./i.test(str.trim());
};

// ── LobbyPickerPopup ──────────────────────────────────────────────────────────
function LobbyPickerPopup({ lobbies = [], currentTargetId, query, onQueryChange, onSelect, onSelectExternal, onClose, anchorEl }) {
  const popupRef = useRef(null);
  const [pos, setPos] = useState({ bottom: 72, left: 0 });

  useEffect(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const popupWidth = 340;
      const viewportWidth = window.innerWidth;
      let left = rect.left;
      if (left + popupWidth > viewportWidth - 8) left = viewportWidth - popupWidth - 8;
      if (left < 8) left = 8;
      setPos({ bottom: window.innerHeight - rect.top + 6, left });
    }
  }, [anchorEl]);

  useEffect(() => {
    const handler = e => {
      if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [onClose]);

  const looksLikeUrl = isUrl(query);
  const filtered = lobbies.filter(l =>
    l.name?.toLowerCase().includes((query || '').toLowerCase())
  );
  const stopAll = e => { e.stopPropagation(); };

  return (
    <div
      ref={popupRef}
      onMouseDown={stopAll}
      onClick={stopAll}
      style={{
        position: 'fixed',
        bottom: pos.bottom, left: pos.left,
        width: 340, maxHeight: 430,
        background: '#fff',
        border: '1px solid #e4e9f0',
        borderRadius: 14,
        boxShadow: '0 12px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9999,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px 8px', borderBottom: '1px solid #f0f4f8', flexShrink: 0,
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#1a1a2e' }}>Set Destination</span>
        <div
          onClick={onClose}
          style={{
            width: 22, height: 22, borderRadius: '50%', background: '#f0f4f8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 12, color: '#9ea8b3', flexShrink: 0,
            transition: 'background 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#f0f4f8'; e.currentTarget.style.color = '#9ea8b3'; }}
        >✕</div>
      </div>

      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <input
            autoFocus
            value={query || ''}
            onChange={e => onQueryChange(e.target.value)}
            placeholder="Search lobbies or paste a URL…"
            style={{
              width: '100%', boxSizing: 'border-box',
              border: `1.5px solid ${looksLikeUrl ? '#2d9e4e' : '#e4e9f0'}`,
              borderRadius: 8, padding: '7px 10px',
              paddingRight: looksLikeUrl ? 72 : 10,
              fontSize: 13, color: '#3c4555',
              outline: 'none', background: '#f8f9fb', fontFamily: 'inherit',
              transition: 'border-color 0.15s',
            }}
          />
          {looksLikeUrl && (
            <div
              onClick={() => { onSelectExternal(query.trim()); onClose(); }}
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: '#2d9e4e', color: '#fff',
                fontSize: 11, fontWeight: 700, padding: '3px 9px',
                borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >Use URL</div>
          )}
        </div>
        {looksLikeUrl && (
          <div style={{ fontSize: 11, color: '#2d9e4e', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>🔗</span> Detected as external link — click Use URL to apply
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px 10px' }}>
        {!looksLikeUrl && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 12, color: '#9ea8b3' }}>
            No lobbies found
          </div>
        )}
        {!looksLikeUrl && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {filtered.map(lobby => {
              const isSelected = lobby._id === currentTargetId;
              const bg = lobby.backgroundImage || lobby.bg || null;
              return (
                <div
                  key={lobby._id}
                  onClick={() => { onSelect(lobby); onClose(); }}
                  style={{
                    borderRadius: 9,
                    border: `2px solid ${isSelected ? PRIMARY : '#e4e9f0'}`,
                    background: isSelected ? alpha(PRIMARY, 0.06) : '#fafbfc',
                    cursor: 'pointer', overflow: 'hidden', transition: 'all 0.14s',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#a0bdd8'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = '#e4e9f0'; }}
                >
                  <div style={{
                    height: 66,
                    background: bg
                      ? `url(${bg}) center/cover no-repeat`
                      : 'linear-gradient(135deg, #edf3fb 0%, #e4ecf4 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative',
                  }}>
                    {!bg && <span style={{ fontSize: 20, opacity: 0.2 }}>🏛</span>}
                    {isSelected && (
                      <div style={{
                        position: 'absolute', top: 5, right: 5,
                        width: 17, height: 17, borderRadius: '50%',
                        background: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{ color: '#fff', fontSize: 9, fontWeight: 800 }}>✓</span>
                      </div>
                    )}
                  </div>
                  <div style={{
                    padding: '5px 7px 7px', fontSize: 11, fontWeight: 600,
                    color: isSelected ? PRIMARY : '#2c3444',
                    lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {lobby.name}
                    {lobby.isDefault && (
                      <span style={{ marginLeft: 4, fontSize: 8.5, fontWeight: 700, background: PRIMARY, color: '#fff', padding: '1px 4px', borderRadius: 3 }}>
                        DEFAULT
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StyleBoxes({ hotspot, upd, stopAll, breakpoint, onUpdateResponsive }) {
  const [openPopover, setOpenPopover] = useState(null);
  const fillRef   = useRef(null);
  const borderRef = useRef(null);
  const widthRef  = useRef(null);

  const isText  = hotspot?.hotspotType === 'text';
  const isImage = hotspot?.hotspotType === 'image';
  const bp      = breakpoint || 'desktop';
  const isDesktop = bp === 'desktop';

  // For text/image elements we always write to desktop (their style lives in meta,
  // not in the responsive override map). For hotspots we go bp-aware.
  const resolvedStyle = resolveHotspotStyleForBp(hotspot, bp);

  // For text elements: drive meta.bgColor / meta.bgStrokeColor / meta.bgStrokeWidth
  // For all others:    drive hoverFillColor / hoverBorderColor / strokeWidth (bp-aware)
  const fillColor     = isText
    ? (() => { const c = hotspot?.meta?.bgColor || 'rgba(0,0,0,0.7)'; const m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/); return m ? '#' + [m[1],m[2],m[3]].map(v => parseInt(v).toString(16).padStart(2,'0')).join('') : (c.startsWith('#') ? c : '#000000'); })()
    : resolvedStyle.hoverFillColor;
  const fillOpacity   = isText
    ? (() => { const c = hotspot?.meta?.bgColor || 'rgba(0,0,0,0.7)'; const m = c.match(/rgba\(\d+,\s*\d+,\s*\d+,\s*([\d.]+)\)/); return m ? parseFloat(m[1]) : (c === 'transparent' ? 0 : 1); })()
    : resolvedStyle.hoverFillOpacity;
  const borderColor   = (isText || isImage)
    ? (hotspot?.meta?.bgStrokeColor && hotspot?.meta?.bgStrokeColor !== 'transparent' ? hotspot.meta.bgStrokeColor : '#ffffff')
    : resolvedStyle.hoverBorderColor;
  const borderOpacity = (isText || isImage) ? 1 : resolvedStyle.hoverBorderOpacity;
  const strokeWidth   = (isText || isImage)
    ? (hotspot?.meta?.bgStrokeWidth ?? (isText ? 3 : 0))
    : resolvedStyle.strokeWidth;

  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1,3),16);
    const g = parseInt(hex.slice(3,5),16);
    const b = parseInt(hex.slice(5,7),16);
    return alpha === 0 ? 'transparent' : `rgba(${r},${g},${b},${alpha})`;
  };

  // Writer: for text/image always write to desktop meta; for hotspots write to bp override
  const updStyle = (patch) => {
    if (isText || isImage || isDesktop) {
      upd(patch);
    } else {
      const updated = setHotspotStyleBpOverride(hotspot, bp, patch);
      onUpdateResponsive ? onUpdateResponsive(hotspot.id, bp, { style: updated.responsive[bp].style }) : upd({ responsive: updated.responsive });
    }
  };

  const updFillColor   = v => isText ? upd({ meta: { ...hotspot.meta, bgColor: hexToRgba(v, fillOpacity) } })       : updStyle({ hoverFillColor: v });
  const updFillOpacity = v => isText ? upd({ meta: { ...hotspot.meta, bgColor: hexToRgba(fillColor, v) } })         : updStyle({ hoverFillOpacity: v });
  const updBorderColor = v => (isText || isImage) ? upd({ meta: { ...hotspot.meta, bgStrokeColor: v } })            : updStyle({ hoverBorderColor: v });
  const updBorderOpacity = v => (isText || isImage) ? undefined                                                     : updStyle({ hoverBorderOpacity: v });
  const updStrokeWidth = v => (isText || isImage) ? upd({ meta: { ...hotspot.meta, bgStrokeWidth: v } })            : updStyle({ strokeWidth: v });

  const toggle = (name) => setOpenPopover(v => v === name ? null : name);

  const BoxBtn = ({ refProp, onClick, label, children, isOpen }) => (
    <Box
      ref={refProp}
      onClick={onClick}
      onMouseDown={stopAll}
      sx={{
        height: 28, px: 1, borderRadius: '7px',
        display: 'flex', alignItems: 'center', gap: '5px',
        cursor: 'pointer', userSelect: 'none',
        border: `1px solid ${isOpen ? PRIMARY : '#e4e9f0'}`,
        background: isOpen ? alpha(PRIMARY, 0.06) : '#f8f9fb',
        transition: 'all 0.13s',
        '&:hover': { borderColor: alpha(PRIMARY, 0.4) },
      }}
    >
      {children}
      <Box sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#5a6474', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
    </Box>
  );

  return (
    <Box onMouseDown={stopAll} onClick={stopAll} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {!isImage && (
        <BoxBtn refProp={fillRef} onClick={() => toggle('fill')} label={`${Math.round(fillOpacity * 100)}%`} isOpen={openPopover === 'fill'}>
          <Box sx={{ width: 16, height: 16, borderRadius: '4px', flexShrink: 0, background: fillColor, border: '1px solid rgba(0,0,0,0.1)' }} />
          <Box sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#9ea8b3', letterSpacing: '0.04em' }}>FILL</Box>
        </BoxBtn>
      )}

      {!isImage && (
        <BoxBtn refProp={borderRef} onClick={() => toggle('border')} label={`${Math.round(borderOpacity * 100)}%`} isOpen={openPopover === 'border'}>
          <Box sx={{ width: 16, height: 16, borderRadius: '4px', flexShrink: 0, background: 'transparent', border: `2.5px solid ${borderColor}` }} />
          <Box sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#9ea8b3', letterSpacing: '0.04em' }}>BORDER</Box>
        </BoxBtn>
      )}

      {!isImage && (
        <BoxBtn refProp={widthRef} onClick={() => toggle('width')} label="" isOpen={openPopover === 'width'}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center' }}>
            {[1, 2, 3].map((_, i) => (
              <Box key={i} sx={{ width: 14, height: `${Math.max(1, Math.min(4, strokeWidth * (i === 1 ? 0.6 : i === 0 ? 0.3 : 0.9)))}px`, borderRadius: '2px', background: '#5a6474' }} />
            ))}
          </Box>
          <Box sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#5a6474' }}>{strokeWidth}px</Box>
        </BoxBtn>
      )}

      {openPopover === 'fill' && (
        <ColorOpacityPopover label="Fill Color" color={fillColor} opacity={fillOpacity} onColorChange={updFillColor} onOpacityChange={updFillOpacity} onClose={() => setOpenPopover(null)} anchorRef={fillRef} />
      )}
      {openPopover === 'border' && (
        <ColorOpacityPopover label="Border Color" color={borderColor} opacity={borderOpacity} onColorChange={updBorderColor} onOpacityChange={(isText || isImage) ? undefined : updBorderOpacity} onClose={() => setOpenPopover(null)} anchorRef={borderRef} hideOpacity={isText || isImage} />
      )}
      {openPopover === 'width' && (
        <WidthPopover value={strokeWidth} onChange={updStrokeWidth} onClose={() => setOpenPopover(null)} anchorRef={widthRef} />
      )}
    </Box>
  );
}

// ── TransitionVideoButton ─────────────────────────────────────────────────────
function TransitionVideoButton({ value, onChange, stopAll, isOverride, bpColor }) {
  const [open, setOpen] = useState(false);
  // value === undefined means "no explicit bp selection yet" (show as unset)
  const hasVideo = !!value;
  const isExplicitlyCleared = value === null && isOverride; // bp override explicitly set to none
  const filename = value ? decodeURIComponent(value.split('/').pop().split('?')[0]) : '';
  const accent   = isOverride ? (bpColor || PRIMARY) : PRIMARY;

  return (
    <>
      <Box sx={{ width: '1px', height: 18, background: '#e8edf2', mx: 0.25, flexShrink: 0 }} />
      <Box
        onClick={e => { stopAll(e); setOpen(true); }}
        onMouseDown={stopAll}
        sx={{
          height: 30, px: 0.75, borderRadius: '7px',
          display: 'flex', alignItems: 'center', gap: '5px',
          border: `1px solid ${open ? accent : hasVideo ? alpha(accent, 0.35) : isOverride ? alpha(accent, 0.25) : '#e4e9f0'}`,
          background: open ? alpha(accent, 0.07) : hasVideo ? alpha(accent, 0.04) : isOverride ? alpha(accent, 0.03) : '#f8f9fb',
          transition: 'all 0.15s', cursor: 'pointer',
          '&:hover': { borderColor: alpha(accent, 0.5) },
        }}
      >
        {hasVideo ? (
          <Box sx={{ width: 22, height: 22, borderRadius: '4px', flexShrink: 0, overflow: 'hidden', border: '1px solid #e4e9f0', background: '#0a0e1a', position: 'relative' }}>
            <video src={value} muted style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <VideocamOutlined sx={{ fontSize: 11, color: 'rgba(255,255,255,0.85)' }} />
            </Box>
          </Box>
        ) : (
          <VideocamOutlined sx={{ fontSize: 15, color: isOverride ? alpha(accent, 0.7) : '#9ea8b3' }} />
        )}
        {hasVideo ? (
          <Box sx={{ fontSize: 11, fontWeight: 600, color: accent, maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {filename}
          </Box>
        ) : isExplicitlyCleared ? (
          <Box sx={{ fontSize: 11, fontWeight: 600, color: alpha(accent, 0.7), whiteSpace: 'nowrap' }}>None</Box>
        ) : (
          <Box sx={{ fontSize: 11, fontWeight: 600, color: '#9ea8b3', whiteSpace: 'nowrap' }}>Transition</Box>
        )}
        {hasVideo && (
          <Box onClick={e => { e.stopPropagation(); onChange(null); }} sx={{ fontSize: 10, color: '#b0bac8', fontWeight: 700, lineHeight: 1, flexShrink: 0, '&:hover': { color: '#dc2626' } }}>✕</Box>
        )}
        {/* Small indicator dot when bp has an explicit override with no video */}
        {isOverride && !hasVideo && (
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: accent, flexShrink: 0, opacity: 0.8 }} />
        )}
      </Box>
      <ContentBrowserModal open={open} onClose={() => setOpen(false)} onSelect={url => { onChange(url); setOpen(false); }} mediaType="video" />
    </>
  );
}

function ExhibitorCardStrip({ ex, hotspot, lobbies, lobbySlides, isCarouselLobby, onUpdateExhibitor, onDeleteExhibitor, onGoToExhibitorLobby, onCreateExhibitorLobby, onAddSlide, resolvedTransition, updTransition, stopAll }) {
  // Single open-panel state — only one popup open at a time
  const [openPanel, setOpenPanel] = useState(null); // 'dest' | 'thumb' | 'logo'
  const [destSearch, setDestSearch] = useState('');

  const destBtnRef  = useRef(null);
  const thumbBtnRef = useRef(null);
  const logoBtnRef  = useRef(null);

  const toggle = (panel) => setOpenPanel(prev => prev === panel ? null : panel);

  // Compute fixed position above a button ref
  const getPos = (ref) => {
    if (!ref.current) return { bottom: 60, left: 0 };
    const r = ref.current.getBoundingClientRect();
    return { bottom: window.innerHeight - r.top + 6, left: r.left };
  };

  const upd = patch => onUpdateExhibitor?.({ ...ex, ...patch });

  const boothAction  = ex.boothAction;
  const destType     = boothAction?.type || (ex.boothLobbyId ? 'lobby' : null);
  const destTargetId = boothAction?.targetId || ex.boothLobbyId || null;
  const hasDestSlide = destType === 'slide' && destTargetId;
  const hasDestLobby = destType === 'lobby' && destTargetId;
  const hasAnyDest   = hasDestSlide || hasDestLobby;

  // Clear stale link if the target slide or lobby no longer exists
  useEffect(() => {
    if (!boothAction?.type || !boothAction?.targetId) return;
    let isStale = false;
    if (boothAction.type === 'slide') {
      isStale = !lobbySlides.some(s => s.id === boothAction.targetId);
    } else if (boothAction.type === 'lobby') {
      isStale = !lobbies.some(l => String(l._id) === String(boothAction.targetId));
    }
    if (isStale) {
      onUpdateExhibitor?.({ ...ex, boothAction: null, boothLobbyId: null });
    }
  }, [boothAction?.type, boothAction?.targetId, lobbies, lobbySlides]); // eslint-disable-line 
  const visitLabel   = hasDestSlide ? '→ Slide' : hasDestLobby ? '→ Lobby' : null;

  // Quick action button — left of Set Destination
  const quickLabel  = isCarouselLobby
    ? (hasDestSlide ? 'Go to Slide' : 'New Slide')
    : (hasDestLobby ? 'Go to Lobby' : 'New Lobby');
  const quickColor  = isCarouselLobby ? '#c2410c' : PRIMARY;
  const quickAction = () => {
    if (isCarouselLobby) {
      if (hasDestSlide) {
        onGoToExhibitorLobby?.({ type: 'slide', targetId: destTargetId });
      } else {
        // Create a new slide and link this exhibitor to it
        onAddSlide?.(ex);
      }
    } else {
      if (hasDestLobby) {
        onGoToExhibitorLobby?.(destTargetId);
      } else {
        // Create a new lobby linked to this exhibitor
        onCreateExhibitorLobby?.(ex);
      }
    }
  };

  const Divider = () => <Box sx={{ width: '1px', height: 18, background: '#e8edf2', mx: 0.25, flexShrink: 0 }} />;

  return (
    <Box onMouseDown={stopAll} onClick={stopAll} sx={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>

      {/* ── Quick action: Create New Slide/Lobby or Go to Slide/Lobby ── */}
      <Box
        onMouseDown={e => { stopAll(e); quickAction(); }}
        sx={{
          height: 30, px: 0.85, borderRadius: '7px', display: 'flex', alignItems: 'center', gap: '5px',
          border: `1px solid ${alpha(quickColor, hasAnyDest ? 0.45 : 0.3)}`,
          background: alpha(quickColor, hasAnyDest ? 0.08 : 0.04),
          cursor: 'pointer', transition: 'all 0.15s',
          '&:hover': { background: alpha(quickColor, 0.14), borderColor: alpha(quickColor, 0.65) },
        }}
      >
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: quickColor, whiteSpace: 'nowrap' }}>
          {hasAnyDest ? '→ ' : '＋ '}{quickLabel}
        </Typography>
      </Box>

      <Divider />

      {/* ── Set Destination ── */}
      <Box ref={destBtnRef}
        onMouseDown={e => { stopAll(e); toggle('dest'); setDestSearch(''); }}
        sx={{
          height: 30, px: 0.85, borderRadius: '7px', display: 'flex', alignItems: 'center', gap: '5px',
          border: `1px solid ${hasAnyDest ? alpha('#0d7a5f', 0.4) : openPanel === 'dest' ? alpha(PRIMARY, 0.5) : '#e4e9f0'}`,
          background: hasAnyDest ? alpha('#0d7a5f', 0.06) : openPanel === 'dest' ? alpha(PRIMARY, 0.06) : '#f8f9fb',
          cursor: 'pointer', transition: 'all 0.15s', '&:hover': { borderColor: alpha('#0d7a5f', 0.5) },
        }}>
        <Box sx={{ fontSize: 12, lineHeight: 1 }}>🔗</Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: hasAnyDest ? '#0d7a5f' : '#9ea8b3', whiteSpace: 'nowrap' }}>
          {hasAnyDest ? 'Destination ✓' : 'Set Destination'}
        </Typography>
      </Box>

      {/* Destination popup — LIGHT MODE, fixed portal */}
      {openPanel === 'dest' && ReactDOM.createPortal(
        <Box
          onMouseDown={e => e.stopPropagation()}
          sx={{
            position: 'fixed', ...getPos(destBtnRef),
            width: 280, zIndex: 1200,
            background: '#fff', borderRadius: '12px',
            border: '1px solid #e4e9f0', boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: 360,
          }}
        >
          {/* Header */}
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e' }}>Set Destination</Typography>
            <Box onMouseDown={e => { e.stopPropagation(); setOpenPanel(null); }}
              sx={{ color: '#9ea8b3', cursor: 'pointer', fontSize: 14, lineHeight: 1, '&:hover': { color: '#333' } }}>✕</Box>
          </Box>

          {/* Search */}
          <Box sx={{ px: 1.5, pt: 1, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.5, borderRadius: '7px', background: '#f5f7fa', border: '1px solid #e8edf2' }}>
              <SearchOutlined sx={{ fontSize: 13, color: '#b0bac8' }} />
              <Box component="input" value={destSearch}
                onChange={e => setDestSearch(e.target.value)}
                onMouseDown={e => e.stopPropagation()}
                placeholder="Search…"
                sx={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: '#1a1a2e', fontSize: '0.72rem', fontFamily: 'inherit', '&::placeholder': { color: '#b0bac8' } }} />
            </Box>
          </Box>

          {/* List */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>

            {/* Slides — carousel lobbies only */}
            {isCarouselLobby && lobbySlides.length > 0 && (() => {
              const filtered = lobbySlides.filter(s => !destSearch.trim() || (s.label || '').toLowerCase().includes(destSearch.toLowerCase()));
              if (!filtered.length) return null;
              return (<>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.08em', px: 0.25, mb: 0.25 }}>Slides</Typography>
                {filtered.map((slide, idx) => {
                  const sel = boothAction?.type === 'slide' && boothAction?.targetId === slide.id;
                  return (
                    <Box key={slide.id}
                      onMouseDown={e => { e.stopPropagation(); upd({ boothAction: { type: 'slide', targetId: slide.id } }); setOpenPanel(null); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.75, py: 0.6, borderRadius: '7px', cursor: 'pointer', background: sel ? alpha('#c2410c', 0.08) : 'transparent', border: `1px solid ${sel ? alpha('#c2410c', 0.3) : 'transparent'}`, '&:hover': { background: alpha('#c2410c', 0.06) } }}>
                      <Box sx={{ width: 24, height: 15, borderRadius: '3px', flexShrink: 0, border: '1px solid #e4e9f0', background: slide.bgUrl ? `url(${slide.bgUrl}) center/cover` : '#f0f4f8', position: 'relative', overflow: 'hidden' }}>
                        <Typography sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.42rem', fontWeight: 700, color: slide.bgUrl ? '#fff' : '#9ea8b3', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{idx + 1}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', color: sel ? '#c2410c' : '#3c4555', fontWeight: sel ? 700 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{slide.label || `Slide ${idx + 1}`}</Typography>
                      {sel && <Box sx={{ fontSize: 11, color: '#c2410c', fontWeight: 700 }}>✓</Box>}
                    </Box>
                  );
                })}
              </>);
            })()}

            {/* Lobbies */}
            {(() => {
              const filtered = lobbies.filter(l => l.lobbyType !== 'booth' && (!destSearch.trim() || l.name?.toLowerCase().includes(destSearch.toLowerCase())));
              if (!filtered.length) return null;
              return (<>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.08em', px: 0.25, mt: isCarouselLobby ? 0.75 : 0, mb: 0.25 }}>Lobbies</Typography>
                {filtered.map(l => {
                  const sel = (boothAction?.type === 'lobby' && boothAction?.targetId === l._id) || (!boothAction && ex.boothLobbyId === l._id);
                  return (
                    <Box key={l._id}
                      onMouseDown={e => { e.stopPropagation(); upd({ boothAction: { type: 'lobby', targetId: l._id }, boothLobbyId: l._id }); setOpenPanel(null); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.75, py: 0.6, borderRadius: '7px', cursor: 'pointer', background: sel ? alpha(PRIMARY, 0.07) : 'transparent', border: `1px solid ${sel ? alpha(PRIMARY, 0.25) : 'transparent'}`, '&:hover': { background: alpha(PRIMARY, 0.05) } }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: l.lobbyType === 'carousel' ? '#c2410c' : l.lobbyType === 'plenary' ? '#8b4fcc' : PRIMARY }} />
                      <Typography sx={{ fontSize: '0.72rem', color: sel ? PRIMARY : '#3c4555', fontWeight: sel ? 700 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</Typography>
                      {sel && <Box sx={{ fontSize: 11, color: PRIMARY, fontWeight: 700 }}>✓</Box>}
                    </Box>
                  );
                })}
              </>);
            })()}

            {/* Clear */}
            {hasAnyDest && (
              <Box onMouseDown={e => { e.stopPropagation(); upd({ boothAction: null, boothLobbyId: null }); setOpenPanel(null); }}
                sx={{ mt: 0.5, px: 0.75, py: 0.6, borderRadius: '7px', cursor: 'pointer', border: '1px solid #fee2e2', '&:hover': { background: '#fff5f5' } }}>
                <Typography sx={{ fontSize: '0.68rem', color: '#dc2626', fontWeight: 600, textAlign: 'center' }}>Clear Destination</Typography>
              </Box>
            )}
          </Box>
        </Box>,
        document.body
      )}

      <Divider />

      {/* ── Transition ── */}
      <TransitionVideoButton value={resolvedTransition ?? ''} onChange={updTransition} stopAll={stopAll} />

      <Divider />

      {/* ── Set Thumbnail ── */}
      <Box ref={thumbBtnRef}
        onMouseDown={e => { stopAll(e); toggle('thumb'); }}
        sx={{ height: 30, px: 0.85, borderRadius: '7px', display: 'flex', alignItems: 'center', gap: '5px', border: `1px solid ${ex.boothThumbnail ? alpha(PRIMARY, 0.35) : openPanel === 'thumb' ? alpha(PRIMARY, 0.5) : '#e4e9f0'}`, background: ex.boothThumbnail ? alpha(PRIMARY, 0.04) : openPanel === 'thumb' ? alpha(PRIMARY, 0.06) : '#f8f9fb', cursor: 'pointer', '&:hover': { borderColor: alpha(PRIMARY, 0.45) } }}>
        {ex.boothThumbnail && <Box sx={{ width: 18, height: 18, borderRadius: '3px', overflow: 'hidden', flexShrink: 0 }}><Box component="img" src={ex.boothThumbnail} sx={{ width: '100%', height: '100%', objectFit: ex.boothThumbnailFit || 'cover' }} /></Box>}
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: ex.boothThumbnail ? PRIMARY : '#9ea8b3', whiteSpace: 'nowrap' }}>Thumbnail</Typography>
      </Box>

      {/* Thumbnail popup — ImagePopover style, fixed portal */}
      {openPanel === 'thumb' && ReactDOM.createPortal(
        <Box onMouseDown={e => e.stopPropagation()}
          sx={{ position: 'fixed', ...getPos(thumbBtnRef), width: 240, zIndex: 1200, background: '#fff', borderRadius: '12px', border: '1px solid #e4e9f0', boxShadow: '0 8px 32px rgba(0,0,0,0.16)', p: '12px 14px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ fontSize: 10, fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Thumbnail</Box>
            <Box onMouseDown={e => { e.stopPropagation(); setOpenPanel(null); }} sx={{ fontSize: 14, lineHeight: 1, color: '#b0bac8', cursor: 'pointer', fontWeight: 700, '&:hover': { color: '#5a6474' } }}>✕</Box>
          </Box>

          {/* Image preview + select */}
          <ThumbPickerInline value={ex.boothThumbnail} onChange={url => upd({ boothThumbnail: url || null })} stopAll={e => e.stopPropagation()} onPanelClose={() => {}} />

          {/* Fit buttons */}
          <Box>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#b0bac8', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fit</Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[
                { value: 'cover',   label: 'Cover',   icon: <CropOutlined sx={{ fontSize: 14 }} /> },
                { value: 'contain', label: 'Contain', icon: <FitScreenOutlined sx={{ fontSize: 14 }} /> },
              ].map(opt => {
                const isSel = (ex.boothThumbnailFit || 'cover') === opt.value;
                return (
                  <Box key={opt.value} onMouseDown={e => { e.stopPropagation(); upd({ boothThumbnailFit: opt.value }); }}
                    sx={{ flex: 1, height: 34, borderRadius: '7px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', border: `1px solid ${isSel ? PRIMARY : '#e4e9f0'}`, background: isSel ? alpha(PRIMARY, 0.07) : '#f8f9fb', color: isSel ? PRIMARY : '#9ea8b3', transition: 'all 0.12s', '&:hover': { borderColor: alpha(PRIMARY, 0.5), color: PRIMARY } }}>
                    {opt.icon}
                    <Box sx={{ fontSize: 9.5, fontWeight: 700 }}>{opt.label}</Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Background color */}
          <Box>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#b0bac8', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Background</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <input type="color" value={ex.boothBgColor || '#1a1a2e'}
                onMouseDown={e => e.stopPropagation()}
                onChange={e => { e.stopPropagation(); upd({ boothBgColor: e.target.value }); }}
                style={{ width: 28, height: 28, border: '1px solid #e4e9f0', borderRadius: 4, padding: 2, cursor: 'pointer', background: 'none' }} />
              <Box component="input" value={ex.boothBgColor || '#1a1a2e'}
                onMouseDown={e => e.stopPropagation()}
                onChange={e => { e.stopPropagation(); upd({ boothBgColor: e.target.value }); }}
                sx={{ flex: 1, border: '1px solid #e4e9f0', borderRadius: '6px', px: 0.75, py: 0.5, fontSize: '0.72rem', fontFamily: 'inherit', outline: 'none', color: '#3c4555', '&:focus': { borderColor: PRIMARY } }} />
            </Box>
          </Box>
        </Box>,
        document.body
      )}

      {/* ── Set Logo ── */}
      <Box ref={logoBtnRef}
        onMouseDown={e => { stopAll(e); toggle('logo'); }}
        sx={{ height: 30, px: 0.85, borderRadius: '7px', display: 'flex', alignItems: 'center', gap: '5px', border: `1px solid ${ex.logoUrl ? alpha('#e67e22', 0.35) : openPanel === 'logo' ? alpha('#e67e22', 0.5) : '#e4e9f0'}`, background: ex.logoUrl ? alpha('#e67e22', 0.04) : openPanel === 'logo' ? alpha('#e67e22', 0.06) : '#f8f9fb', cursor: 'pointer', '&:hover': { borderColor: alpha('#e67e22', 0.45) } }}>
        {ex.logoUrl && <Box sx={{ width: 18, height: 18, borderRadius: '3px', overflow: 'hidden', flexShrink: 0, background: '#fff', border: '1px solid #e4e9f0' }}><Box component="img" src={ex.logoUrl} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} /></Box>}
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: ex.logoUrl ? '#e67e22' : '#9ea8b3', whiteSpace: 'nowrap' }}>Logo</Typography>
      </Box>

      {/* Logo popup — fixed portal */}
      {openPanel === 'logo' && ReactDOM.createPortal(
        <Box onMouseDown={e => e.stopPropagation()}
          sx={{ position: 'fixed', ...getPos(logoBtnRef), width: 210, zIndex: 1200, background: '#fff', borderRadius: '12px', border: '1px solid #e4e9f0', boxShadow: '0 8px 32px rgba(0,0,0,0.16)', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a1a2e' }}>Logo</Typography>
            <Box onMouseDown={e => { e.stopPropagation(); setOpenPanel(null); }} sx={{ color: '#9ea8b3', cursor: 'pointer', fontSize: 13, '&:hover': { color: '#333' } }}>✕</Box>
          </Box>
          <ThumbPickerInline value={ex.logoUrl} onChange={url => upd({ logoUrl: url || null })} stopAll={e => e.stopPropagation()} />
        </Box>,
        document.body
      )}

      <Divider />

      {/* ── Delete ── */}
      <Box onMouseDown={e => { stopAll(e); onDeleteExhibitor?.(ex._id); }}
        sx={{ height: 30, width: 30, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(220,38,38,0.3)', background: '#fff5f5', cursor: 'pointer', flexShrink: 0, '&:hover': { background: '#fee2e2', borderColor: '#fca5a5' } }}>
        <DeleteOutlineOutlined sx={{ fontSize: 15, color: '#dc2626' }} />
      </Box>
    </Box>
  );
}

// ── HotspotStripContents ──────────────────────────────────────────────────────
export function HotspotStripContents({ hotspot, lobbies = [], onUpdate, onDelete, breakpoint, onUpdateResponsive, selectedLobbyExhibitorId, exhibitors = [], onUpdateExhibitor, onDeleteExhibitor, onGoToExhibitorLobby, onCreateExhibitorLobby, onAddSlide, lobbySlides = [], isCarouselLobby = false }) {
  const [pickerOpen,  setPickerOpen]  = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fontOpen,    setFontOpen]    = useState(false);
  const [imageOpen,   setImageOpen]   = useState(false);
  const destBtnRef = useRef(null);
  const fontBtnRef = useRef(null);
  const imageBtnRef = useRef(null);

  const hotspotId   = hotspot?.id;
  const hotspotType = hotspot?.hotspotType  || 'link';
  const linkTarget  = hotspot?.hotspotAction?.linkTarget || 'lobby';
  const targetId    = hotspot?.hotspotAction?.targetId   || null;
  const externalUrl = hotspot?.hotspotAction?.externalUrl || '';

  // Image elements share the same link/transition UI as link hotspots
  const isImageWithLink = hotspotType === 'image';

  const prevHotspotId = useRef(hotspotId);
  useEffect(() => {
    if (prevHotspotId.current !== hotspotId) {
      setPickerOpen(false);
      setSearchQuery('');
      prevHotspotId.current = hotspotId;
    }
  }, [hotspotId]);

  const selectedLobby = (hotspotType === 'link' || hotspotType === 'image') && linkTarget !== 'external'
    ? lobbies.find(l => l._id === targetId) || null
    : null;
  const hasDestination = selectedLobby || (linkTarget === 'external' && externalUrl);

  const setType = key => {
    if (!hotspot) return;
    onUpdate({ ...hotspot, hotspotType: key, hotspotAction: {} });
  };

  const updAction = patch => {
    if (!hotspot) return;
    onUpdate({ ...hotspot, hotspotAction: { ...hotspot.hotspotAction, ...patch } });
  };

  const upd = patch => {
    if (!hotspot) return;
    onUpdate({ ...hotspot, ...patch });
  };

  const clearDestination = e => {
    e.stopPropagation();
    updAction({ linkTarget: 'lobby', targetId: null, externalUrl: null });
    setPickerOpen(false);
  };

  const stopAll = e => { e.stopPropagation(); };

  const bp        = breakpoint || 'desktop';
  const isDesktop = bp === 'desktop';

  // Determine if we have any style/transition overrides on this bp (for the Reset button)
  const hasStyleOverride      = hasHotspotStyleBpOverride(hotspot, bp);
  const hasTransitionOverride = hasHotspotTransitionBpOverride(hotspot, bp);
  const hasBpOverride         = !isDesktop && (hasStyleOverride || hasTransitionOverride);

  // Resolved transition for the active bp:
  //   desktop → use hotspotAction.transitionVideoUrl
  //   non-desktop → use bp-level override if set; undefined = "no explicit selection yet"
  const resolvedTransition = resolveHotspotTransitionForBp(hotspot, bp);

  const updTransition = (url) => {
    if (isDesktop) {
      upd({ hotspotAction: { ...hotspot.hotspotAction, transitionVideoUrl: url || null } });
    } else {
      const updated = setHotspotTransitionBpOverride(hotspot, bp, url || null);
      onUpdate(updated);
    }
  };

  const handleResetBpStyle = () => {
    if (isDesktop || !hotspot) return;
    onUpdate(clearHotspotStyleBpOverride(hotspot, bp));
  };

  // ── Exhibitor grid toolbar ────────────────────────────────────────────────
  if (hotspotType === 'exhibitor-grid' && selectedLobbyExhibitorId) {
    const ex = exhibitors.find(e => e._id === selectedLobbyExhibitorId);
    if (ex) {
      return (
        <ExhibitorCardStrip
          ex={ex}
          hotspot={hotspot}
          lobbies={lobbies}
          lobbySlides={lobbySlides}
          isCarouselLobby={isCarouselLobby}
          onUpdateExhibitor={onUpdateExhibitor}
          onDeleteExhibitor={onDeleteExhibitor}
          onGoToExhibitorLobby={onGoToExhibitorLobby}
          onCreateExhibitorLobby={onCreateExhibitorLobby}
          onAddSlide={onAddSlide}
          resolvedTransition={resolvedTransition}
          updTransition={updTransition}
          stopAll={stopAll}
        />
      );
    }
  }

  return (
    <>
      <Box onMouseDown={stopAll} onClick={stopAll} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {hotspotType !== 'text' && hotspotType !== 'image' && (
          <TypeDropdown value={hotspotType} onChange={setType} stopAll={stopAll} />
        )}

        {hotspotType === 'link' && (
          <>
            <Box sx={{ width: '1px', height: 18, background: '#e8edf2', mx: 0.25, flexShrink: 0 }} />
            <Box
              ref={destBtnRef}
              onClick={() => setPickerOpen(v => !v)}
              sx={{
                height: 30, minWidth: 110, maxWidth: 220,
                display: 'flex', alignItems: 'center', gap: '6px',
                px: 0.75, borderRadius: '7px', cursor: 'pointer',
                border: `1px solid ${pickerOpen ? PRIMARY : hasDestination ? alpha(PRIMARY, 0.35) : '#e4e9f0'}`,
                background: pickerOpen ? alpha(PRIMARY, 0.07) : hasDestination ? alpha(PRIMARY, 0.04) : '#f8f9fb',
                transition: 'all 0.15s',
                '&:hover': { borderColor: alpha(PRIMARY, 0.5) },
                overflow: 'hidden',
              }}
            >
              {selectedLobby && (
                <Box sx={{ width: 22, height: 22, borderRadius: '3px', flexShrink: 0, background: (selectedLobby.bgUrl || selectedLobby.backgroundImage || selectedLobby.bg) ? `url(${selectedLobby.bgUrl || selectedLobby.backgroundImage || selectedLobby.bg}) center/cover` : alpha(PRIMARY, 0.12), border: '1px solid #e4e9f0' }} />
              )}
              {selectedLobby ? (
                <Box sx={{ fontSize: 12, fontWeight: 600, color: PRIMARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{selectedLobby.name}</Box>
              ) : linkTarget === 'external' && externalUrl ? (
                <Box sx={{ fontSize: 11.5, color: '#2d9e4e', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>🔗 {externalUrl}</Box>
              ) : (
                <Box sx={{ fontSize: 12, color: '#9ea8b3', fontStyle: 'italic', flex: 1, minWidth: 0 }}>Set destination…</Box>
              )}
              {hasDestination && (
                <Box onClick={clearDestination} sx={{ fontSize: 10, color: '#b0bac8', fontWeight: 700, lineHeight: 1, flexShrink: 0, '&:hover': { color: '#dc2626' } }}>✕</Box>
              )}
            </Box>
          </>
        )}

        {hotspotType === 'link' && linkTarget !== 'external' && (
          <TransitionVideoButton
            value={resolvedTransition ?? ''}
            onChange={updTransition}
            stopAll={stopAll}
            isOverride={!isDesktop && hasTransitionOverride}
            bpColor={!isDesktop ? (LOBBY_BREAKPOINTS.find(b => b.key === bp) || LOBBY_BREAKPOINTS[0]).color : undefined}
          />
        )}

        {/* Image element — image button + link destination + transition */}
        {hotspotType === 'image' && (
          <>
            <Box sx={{ width: '1px', height: 18, background: '#e8edf2', mx: 0.25, flexShrink: 0 }} />
            <Box
              ref={imageBtnRef}
              onClick={e => { stopAll(e); setImageOpen(v => !v); }}
              onMouseDown={stopAll}
              sx={{
                height: 30, px: 0.85, borderRadius: '7px',
                display: 'flex', alignItems: 'center', gap: '5px',
                border: `1px solid ${imageOpen ? '#e67e22' : '#e4e9f0'}`,
                background: imageOpen ? alpha('#e67e22', 0.07) : '#f8f9fb',
                cursor: 'pointer', transition: 'all 0.13s',
                '&:hover': { borderColor: alpha('#e67e22', 0.5) },
              }}
            >
              <ImageOutlined sx={{ fontSize: 15, color: imageOpen ? '#e67e22' : '#5a6474' }} />
              <Box sx={{ fontSize: 11, fontWeight: 600, color: imageOpen ? '#e67e22' : '#5a6474', whiteSpace: 'nowrap' }}>Set Image</Box>
            </Box>
            <Box
              ref={destBtnRef}
              onClick={() => setPickerOpen(v => !v)}
              sx={{
                height: 30, minWidth: 110, maxWidth: 220,
                display: 'flex', alignItems: 'center', gap: '6px',
                px: 0.75, borderRadius: '7px', cursor: 'pointer',
                border: `1px solid ${pickerOpen ? PRIMARY : hasDestination ? alpha(PRIMARY, 0.35) : '#e4e9f0'}`,
                background: pickerOpen ? alpha(PRIMARY, 0.07) : hasDestination ? alpha(PRIMARY, 0.04) : '#f8f9fb',
                transition: 'all 0.15s',
                '&:hover': { borderColor: alpha(PRIMARY, 0.5) },
                overflow: 'hidden',
              }}
            >
              {selectedLobby && (
                <Box sx={{ width: 22, height: 22, borderRadius: '3px', flexShrink: 0, background: (selectedLobby.bgUrl || selectedLobby.backgroundImage || selectedLobby.bg) ? `url(${selectedLobby.bgUrl || selectedLobby.backgroundImage || selectedLobby.bg}) center/cover` : alpha(PRIMARY, 0.12), border: '1px solid #e4e9f0' }} />
              )}
              {selectedLobby ? (
                <Box sx={{ fontSize: 12, fontWeight: 600, color: PRIMARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{selectedLobby.name}</Box>
              ) : linkTarget === 'external' && externalUrl ? (
                <Box sx={{ fontSize: 11.5, color: '#2d9e4e', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>🔗 {externalUrl}</Box>
              ) : (
                <Box sx={{ fontSize: 12, color: '#9ea8b3', fontStyle: 'italic', flex: 1, minWidth: 0 }}>Set link…</Box>
              )}
              {hasDestination && (
                <Box onClick={clearDestination} sx={{ fontSize: 10, color: '#b0bac8', fontWeight: 700, lineHeight: 1, flexShrink: 0, '&:hover': { color: '#dc2626' } }}>✕</Box>
              )}
            </Box>
            {linkTarget !== 'external' && (
              <TransitionVideoButton
                value={resolvedTransition ?? ''}
                onChange={updTransition}
                stopAll={stopAll}
                isOverride={!isDesktop && hasTransitionOverride}
                bpColor={!isDesktop ? (LOBBY_BREAKPOINTS.find(b => b.key === bp) || LOBBY_BREAKPOINTS[0]).color : undefined}
              />
            )}
          </>
        )}

        <Box sx={{ width: '1px', height: 18, background: '#e8edf2', mx: 0.25, flexShrink: 0 }} />

        {/* Font button — text elements only, before fill */}
        {hotspotType === 'text' && (
          <>
            <Box
              ref={fontBtnRef}
              onClick={e => { stopAll(e); setFontOpen(v => !v); }}
              onMouseDown={stopAll}
              sx={{
                height: 30, px: 0.85, borderRadius: '7px',
                display: 'flex', alignItems: 'center', gap: '5px',
                border: `1px solid ${fontOpen ? '#7c3aed' : '#e4e9f0'}`,
                background: fontOpen ? alpha('#7c3aed', 0.07) : '#f8f9fb',
                cursor: 'pointer', transition: 'all 0.13s',
                '&:hover': { borderColor: alpha('#7c3aed', 0.5) },
              }}
            >
              <TextFieldsOutlined sx={{ fontSize: 15, color: fontOpen ? '#7c3aed' : '#5a6474' }} />
              <Box sx={{ fontSize: 11, fontWeight: 600, color: fontOpen ? '#7c3aed' : '#5a6474', whiteSpace: 'nowrap' }}>Text Style</Box>
            </Box>
            <Box sx={{ width: '1px', height: 18, background: '#e8edf2', mx: 0.25, flexShrink: 0 }} />
          </>
        )}

        <StyleBoxes hotspot={hotspot} upd={upd} stopAll={stopAll} breakpoint={bp} onUpdateResponsive={onUpdateResponsive} />

        {/* Reset style button — only visible when in a non-desktop bp with overrides */}
        {hasBpOverride && (
          <>
            <Box sx={{ width: '1px', height: 18, background: '#e8edf2', mx: 0.25, flexShrink: 0 }} />
            <Box
              onMouseDown={stopAll}
              onClick={e => { stopAll(e); handleResetBpStyle(); }}
              sx={{
                height: 28, px: 1, borderRadius: '7px',
                display: 'flex', alignItems: 'center', gap: '4px',
                cursor: 'pointer', userSelect: 'none',
                border: '1px solid #fee2e2',
                background: '#fff5f5',
                transition: 'all 0.13s',
                '&:hover': { borderColor: '#fca5a5', background: '#fee2e2' },
              }}
            >
              <Box sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#dc2626', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                Reset Style
              </Box>
            </Box>
          </>
        )}
      </Box>

      {fontOpen && hotspot?.hotspotType === 'text' && (
        <FontPopover
          hotspot={hotspot}
          onUpdate={onUpdate}
          onClose={() => setFontOpen(false)}
          anchorRef={fontBtnRef}
          breakpoint={bp}
        />
      )}

      {imageOpen && hotspot?.hotspotType === 'image' && (
        <ImagePopover
          hotspot={hotspot}
          onUpdate={onUpdate}
          onClose={() => setImageOpen(false)}
          anchorRef={imageBtnRef}
        />
      )}

      {pickerOpen && hotspot && (
        <LobbyPickerPopup
          lobbies={lobbies}
          currentTargetId={targetId}
          query={searchQuery}
          onQueryChange={setSearchQuery}
          anchorEl={destBtnRef.current}
          onSelect={lobby => { updAction({ linkTarget: 'lobby', targetId: lobby._id, externalUrl: null }); setPickerOpen(false); }}
          onSelectExternal={url => { updAction({ linkTarget: 'external', externalUrl: url, targetId: null }); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
}

